import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Outlet, useLocation, useOutletContext } from 'react-router-dom';

import { UseOutletContext } from '../Main';
import { User } from '../../../models/user.model';
import { AppServiceToken} from '../../../models/app.service.model';
import { ApiDigitalAuthUser } from '../../../services/auth/user.service';
import { ApiDigitalAdminContact } from '../../../services/admin/contact.service';
import { Company, CompanyToken}  from '../../../models/company.model';
import { Contact, ContactsData } from '../../../models/contact.model';
import { Settings } from '../../../models/settings.model';
import { Error } from '../../../models/error.model';
import { TreasuryAccount } from '../../../models/treasury.account.model';
import { AccountingAccount } from '../../../models/accounting.account.model';
import { AccountingEntry } from '../../../models/accounting.entry.model';
import { AccountingAccountDA } from '../../../services/admin/accounting.account.service';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { CompanyAccountingAccounts } from '../../../models/company.accounting.accounts.model';
import { GlobalSwal } from '../../../styles/swal.style';
import { getError } from '../../../languages/translations/response';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';
import { normalizeContacts } from '../../../scripts/format.value.script';

export interface ContactPageProps {};

type ContexType = {
  setRoute: Dispatch<SetStateAction<{path: {root: string, branch: string} | null, company: boolean} | null>>,
  userLoggedIn: User | null,
  appServiceForUser: AppServiceToken | null,
  companyForUser: CompanyToken | null,
  settingsForCompany: Settings | null,
  accountingAccountsForCompany: AccountingAccount[] | null,
  accountingCreditEntriesForCompany: AccountingEntry[] | null,
  accountingDebitEntriesForCompany: AccountingEntry[] | null,
  treasuryAccountsForCompany: TreasuryAccount[] | null,
  contactsForCompany: Contact[] | null,
  accountingExpenseAccountsForCompany: AccountingAccount[] | null,
  accountingIncomeAccountsForCompany: AccountingAccount[] | null,
  accountingTaxAccountsForCompany: AccountingAccount[] | null,
  setAccountingExpenseAccountsForCompany: Dispatch<SetStateAction<AccountingAccount[] | null>>,
  setAccountingIncomeAccountsForCompany: Dispatch<SetStateAction<AccountingAccount[] | null>>,
  setAccountingTaxAccountsForCompany: Dispatch<SetStateAction<AccountingAccount[] | null>>,
  loadCompanyForUser: Function,
  loadAccountingCreditEntriesForCompany: Function,
  loadAccountingDebitEntriesForCompany: Function,
  loadContactsForCompany: Function,
  meCompanies: Company[] | null,
  allContacts: Contact[] | null,
  countriesOfContacts: string[] | null,
  loadAllContacts: Function
};

let errorResponse: Error, companiesResponse: any, contactsResponse: ContactsData, accountingIncomeAccountsResponse: CompanyAccountingAccounts, accountingExpenseAccountsResponse: CompanyAccountingAccounts, accountingTaxAccountsResponse: CompanyAccountingAccounts;

const ContactPage: React.FunctionComponent<ContactPageProps> = props => {
  const {setRoute, userLoggedIn, appServiceForUser, companyForUser, settingsForCompany, accountingAccountsForCompany, accountingCreditEntriesForCompany, accountingDebitEntriesForCompany, treasuryAccountsForCompany, contactsForCompany, loadCompanyForUser, loadAccountingCreditEntriesForCompany, loadAccountingDebitEntriesForCompany, loadContactsForCompany} = UseOutletContext()
  const {lang} = StateLanguage()
  const location = useLocation()

  const [meCompanies, setMeCompanies] = useState<Company[] | null>(null)
  const [allContacts, setAllContacts] = useState<Contact[] | null>(null)
  const [countriesOfContacts, setCountriesOfContacts] = useState<string[] | null>(null)
  const [accountingExpenseAccountsForCompany, setAccountingExpenseAccountsForCompany] = useState<AccountingAccount[] | null>(null)
  const [accountingIncomeAccountsForCompany, setAccountingIncomeAccountsForCompany] = useState<AccountingAccount[] | null>(null)
  const [accountingTaxAccountsForCompany, setAccountingTaxAccountsForCompany] = useState<AccountingAccount[] | null>(null)

  const loadMeCompanies = async (api_key: string) => {
    await ApiDigitalAuthUser.getMeCompanies(api_key).then( (response) => {
      if (response.status === 200) {
        companiesResponse = response.data
        const payload: any = companiesResponse
        const list = Array.isArray(payload) ? payload : (payload?.companies ?? payload?.items ?? payload?.data ?? [])
        setMeCompanies(list)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadAllContacts = async (id_company: string) => {
    await ApiDigitalAdminContact.getContacts(id_company, '', '', '', '', 1, 100000).then( (response) => {
      if (response.status === 200) {
        contactsResponse = response.data

        const items = normalizeContacts(contactsResponse.items)
        setAllContacts(items)
        setCountriesOfContacts(Array.from(new Set(items.map((item) => (item.data.address.country)))))
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadAccountingIncomeAccountsForCompany = async (id_company: string) => {
    await AccountingAccountDA.getAccountingAccounts(id_company, [], ['3'], []).then( (response) => {
      if (response.status === 200) {
        accountingIncomeAccountsResponse = response.data
        setAccountingIncomeAccountsForCompany(accountingIncomeAccountsResponse.accounting_accounts)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadAccountingExpenseAccountsForCompany = async (id_company: string) => {
    await AccountingAccountDA.getAccountingAccounts(id_company, [], ['4','6'], []).then( (response) => {
      if (response.status === 200) {
        accountingExpenseAccountsResponse = response.data
        setAccountingExpenseAccountsForCompany(accountingExpenseAccountsResponse.accounting_accounts)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadAccountingTaxAccountsForCompany = async (id_company: string) => {
    await AccountingAccountDA.getAccountingAccounts(id_company, ['1170', '1171'], [], []).then( (response) => {
      if (response.status === 200) {
        accountingTaxAccountsResponse = response.data
        setAccountingTaxAccountsForCompany(accountingTaxAccountsResponse.accounting_accounts)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  useEffect( () => {
    if (companyForUser && location.pathname.includes('/entry')) {
      loadAccountingIncomeAccountsForCompany(companyForUser.id)
      loadAccountingExpenseAccountsForCompany(companyForUser.id)
      loadAccountingTaxAccountsForCompany(companyForUser.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, location.pathname])

  useEffect( () => {
    if (companyForUser) {
      loadAllContacts(companyForUser.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    if (appServiceForUser) {
      loadMeCompanies(appServiceForUser.api_key)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appServiceForUser])

  return (
    <Outlet context={{setRoute, userLoggedIn, appServiceForUser, companyForUser, meCompanies, allContacts, countriesOfContacts, loadAllContacts, settingsForCompany, accountingAccountsForCompany, accountingCreditEntriesForCompany, accountingDebitEntriesForCompany, treasuryAccountsForCompany, contactsForCompany, accountingExpenseAccountsForCompany, accountingIncomeAccountsForCompany, accountingTaxAccountsForCompany, setAccountingExpenseAccountsForCompany, setAccountingIncomeAccountsForCompany, setAccountingTaxAccountsForCompany, loadCompanyForUser, loadAccountingCreditEntriesForCompany, loadAccountingDebitEntriesForCompany, loadContactsForCompany}}></Outlet>
  );
}

export function UseOutletContextContact() {
  return useOutletContext<ContexType>()
};

export default ContactPage;
